import {
  BadgeIcon,
  type BadgeIconType,
} from '@workspace/ui/components/BadgeIcons';
import { Typography } from '@workspace/ui/components/Typography';
import { cn } from '@workspace/ui/lib/utils';
import { useState } from 'react';

import { CollapsibleList } from './CollapsibleList';

const BADGE_CODE_MAP: Record<string, BadgeIconType> = {
  vegan: 'vegan',
  for_vegan: 'vegan',
  organic: 'organic',
  is_organic_food: 'organic',
  halal: 'halal',
  for_halal: 'halal',
  kosher: 'kosher',
  for_kosher: 'kosher',
  fairtrade: 'fairtrade',
  vegeterian: 'vegeterian',
  vegetarian: 'vegeterian',
  for_vegetarians: 'vegeterian',
  dairy_free: 'dairy-free',
  no_dairy: 'dairy-free',
  gluten_free: 'gluten-free',
  no_gluten: 'gluten-free',
  allergen_free: 'allergen-free',
  low_fat: 'low-fat',
  gmo_free: 'gmo-free',
  no_gmo: 'gmo-free',
  contains_sulphites: 'contains-sulphites',
  sulphites: 'contains-sulphites',
  with_sulphites: 'contains-sulphites',
  sugar_free: 'sugar-free',
  no_sugar: 'sugar-free',
  no_added_sugar: 'sugar-free',
  suitable_for_freezing: 'suitable-for-freezing',
  freezing: 'suitable-for-freezing',
  contains_soya: 'contains-soya',
  soya: 'contains-soya',
  with_soya: 'contains-soya',
  may_contain_nuts: 'may-contain-nuts',
  nuts: 'may-contain-nuts',
  only_for_adults: 'only-for-adults',
  adults_only: 'only-for-adults',
};

const COLLAPSE_THRESHOLD = 5;
const COLLAPSE_VISIBLE_COUNT = 3;

export interface CharacteristicsSubItem {
  name: string;
  value: string | number | boolean | string[];
  extraValue?: string;
}

export interface CharacteristicsProperty {
  code: string;
  name: string;
  value: string | number | boolean | string[];
  extraValue?: string;
  subItems?: CharacteristicsSubItem[];
}

export interface CharacteristicsLabels {
  characteristic: string;
  characteristicValue: string;
  extraHeader?: string;
  seeAll: string;
  showLess: string;
}

export interface CharacteristicsProps {
  propertyList: CharacteristicsProperty[];
  badgeList?: CharacteristicsProperty[];
  isMarketplace?: boolean;
  showHeader?: boolean;
  borderless?: boolean;
  labels: CharacteristicsLabels;
}

const Characteristics = ({
  propertyList,
  badgeList = [],
  isMarketplace = false,
  showHeader = true,
  borderless = false,
  labels,
}: CharacteristicsProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const shouldCollapse =
    isMarketplace && propertyList.length > COLLAPSE_THRESHOLD;
  const visibleProperties = shouldCollapse
    ? propertyList.slice(0, COLLAPSE_VISIBLE_COUNT)
    : propertyList;
  const hiddenProperties = shouldCollapse
    ? propertyList.slice(COLLAPSE_VISIBLE_COUNT)
    : [];

  const visibleBadges = badgeList.filter(({ code }) => code in BADGE_CODE_MAP);

  const renderRow = (property: CharacteristicsProperty) => {
    const hasSubItems = property.subItems && property.subItems.length > 0;

    return (
      <div
        key={property.code}
        className={cn(
          'flex flex-row flex-nowrap items-start justify-start gap-4 border-b last:border-b-0',
          borderless
            ? 'border-stroke-quaternary py-3'
            : 'border-stroke-tertiary px-4 py-4',
        )}
      >
        <Typography.Text200Regular
          as="div"
          className={cn(
            'shrink-0 basis-[var(--key-column-width)]',
            borderless ? 'text-text-secondary' : 'text-text-primary',
            hasSubItems && 'flex flex-col gap-1',
          )}
        >
          {hasSubItems ? (
            <>
              <span>{property.name}</span>
              {property.subItems!.map((sub) => (
                <span key={sub.name}>{sub.name}</span>
              ))}
            </>
          ) : (
            property.name
          )}
        </Typography.Text200Regular>
        <Typography.Text200Regular
          as="div"
          className={cn(
            'min-w-0 flex-[1_0_0] whitespace-pre-line text-text-primary',
            hasSubItems && 'flex flex-col gap-1',
          )}
        >
          {hasSubItems ? (
            <>
              <span>{property.value}</span>
              {property.subItems!.map((sub) => (
                <span key={sub.name}>{sub.value}</span>
              ))}
            </>
          ) : (
            property.value
          )}
        </Typography.Text200Regular>
        {labels.extraHeader && (
          <Typography.Text200Regular
            as="div"
            className={cn(
              'min-w-0 flex-[1_0_0] whitespace-pre-line text-text-primary',
              hasSubItems && 'flex flex-col gap-1',
            )}
          >
            {hasSubItems ? (
              <>
                <span>{property.extraValue}</span>
                {property.subItems!.map((sub) => (
                  <span key={sub.name}>{sub.extraValue}</span>
                ))}
              </>
            ) : (
              property.extraValue
            )}
          </Typography.Text200Regular>
        )}
      </div>
    );
  };

  return (
    <>
      {visibleBadges.length > 0 && (
        <div
          className="mb-4 grid grid-cols-5 gap-4 py-2"
          data-testid="badgesv2"
        >
          {visibleBadges.map(({ code, name }) => (
            <div
              key={code}
              className="flex min-w-0 flex-col items-center gap-1 self-start"
            >
              <BadgeIcon
                type={BADGE_CODE_MAP[code]!}
                className="size-6 shrink-0 text-text-primary"
              />
              <Typography.Text50Medium
                as="span"
                className="break-words text-center text-text-primary tracking-[-0.1px]"
              >
                {name}
              </Typography.Text50Medium>
            </div>
          ))}
        </div>
      )}
      {propertyList.length > 0 && (
        <div
          className={cn(
            'relative flex w-full flex-col items-stretch justify-start overflow-hidden',
            borderless
              ? 'mt-2 [--key-column-width:120px]'
              : 'mt-4 rounded-3 border border-stroke-tertiary [--key-column-width:128px]',
            shouldCollapse && 'collapsible-text',
            shouldCollapse && !isExpanded && 'clamp',
          )}
        >
          {showHeader && (
            <div className="flex flex-row flex-nowrap items-start justify-start gap-4 border-b-0 px-4 py-4 pb-2">
              <Typography.Text200Semibold
                as="div"
                className="shrink-0 basis-[var(--key-column-width)] text-text-primary"
              >
                {labels.characteristic}
              </Typography.Text200Semibold>
              <Typography.Text200Semibold
                as="div"
                className="min-w-0 flex-[1_0_0] whitespace-pre-line text-text-primary"
              >
                {labels.characteristicValue}
              </Typography.Text200Semibold>
              {labels.extraHeader && (
                <Typography.Text200Semibold
                  as="div"
                  className="min-w-0 flex-[1_0_0] whitespace-pre-line text-text-primary"
                >
                  {labels.extraHeader}
                </Typography.Text200Semibold>
              )}
            </div>
          )}
          {isMarketplace ? (
            <CollapsibleList
              itemsToShow={visibleProperties.map(renderRow)}
              itemsToHide={hiddenProperties.map(renderRow)}
              showMoreText={labels.seeAll}
              showLessText={labels.showLess}
              sublistClassNameCollapsible="h-0 overflow-hidden"
              buttonContainerClassName="flex justify-start"
              buttonClassName="appearance-none border-none bg-transparent p-0 font-[var(--text-300-medium-high)] text-text-secondary h-auto underline cursor-pointer"
              onToggle={setIsExpanded}
            />
          ) : (
            propertyList.map(renderRow)
          )}
        </div>
      )}
    </>
  );
};

export default Characteristics;
